import boom from 'boom'
import { logError, logWarn } from 'lib/logging'

/**
 * Supported Boom error types
 */
export type boomTypes =
  | `badRequest`
  | `badImplementation`
  | `unauthorized`
  | `forbidden`
  | `notFound`
  | `expectationFailed`

/**
 * Asserts that the predicate is true. If it is not, throws the specified type of boom error
 * @param {unknown} predicate - The condition to assert
 * @param {string} [message] - Optional error message
 * @param {boomTypes} [boomType='badImplementation'] - The type of boom error to throw
 * @throws {boom.Boom} Throws the specified boom error if predicate is falsy
 */
export function assertBoom(
  predicate: unknown,
  message?: string,
  boomType: boomTypes = `badImplementation`,
): asserts predicate {
  if (!predicate) {
    try {
      throw (boom[boomType] as any)(message)
    } catch (err) {
      if (boomType === `unauthorized`) {
        logWarn(`[auth error]`, err)
      } else if (boomType === `badImplementation`) {
        logError(`[server error]`, err)
      }
      throw err
    }
  }
}

/**
 * Asserts that the predicate is true, throwing a badRequest error if not
 * @param {unknown} predicate - The condition to assert
 * @param {...any} message - Error message parts that will be joined with spaces
 * @throws {boom.Boom} Throws a badRequest error if predicate is falsy
 */
export function assertGood(predicate: unknown, ...message: any): asserts predicate {
  assertBoom(predicate, message?.map((i) => String(i)).join(` `) || `bad request`, `badRequest`)
}
/**
 * Asserts that the predicate is true, throwing an unauthorized error if not
 * @param {unknown} predicate - The condition to assert
 * @param {...any} message - Error message parts for logging
 * @throws {boom.Boom} Throws an unauthorized error if predicate is falsy
 */
export function assertAllowed(predicate: unknown, ...message: any): asserts predicate {
  if (!predicate) {
    logWarn(`[auth error]`, message)
    throw boom.unauthorized()
  }
}

/**
 * Asserts that the predicate is true, throwing a notFound error if not
 * @param {unknown} predicate - The condition to assert
 * @param {string} [message='not found'] - Optional error message
 * @throws {boom.Boom} Throws a notFound error if predicate is falsy
 */
export function assertFound(predicate: unknown, message?: string): asserts predicate {
  assertBoom(predicate, message || `not found`, `notFound`)
}
